import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  MonthYearSelector,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@travellegacy/nexops-design-system';
import { Loader2, DollarSign, Users, QrCode, Copy, CheckCircle2, ArrowUpDown, ArrowUp, ArrowDown, Check, X, Lock, Gift, Plus } from 'lucide-react';
import { EmployeeAdditionalsDialog } from '../components/EmployeeAdditionalsDialog';
import { employeePaymentsAPI } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import { toast } from '@/shared/lib/toast';
import type { EmployeePaymentSummary, EmployeeBenefitSummary, PixQRCodeResult } from '@/shared/types';

type SortField = 'name' | 'salary' | 'commissions' | 'additionals' | 'profitAdvance' | 'total';
type SortDirection = 'asc' | 'desc' | null;

/** Itens da coluna Adicionais: VT pago no Pix (automático) + adicionais lançados à mão */
function additionalsItems(payment: EmployeePaymentSummary): { description: string; amount: number }[] {
  const vt = payment.details.vtPixAmount ? [{ description: 'Vale Transporte (Pix)', amount: payment.details.vtPixAmount }] : [];
  return [...vt, ...(payment.details.additionals ?? [])];
}

function additionalsTotal(payment: EmployeePaymentSummary): number {
  return (payment.details.additionalsAmount || 0) + (payment.details.vtPixAmount || 0);
}

export default function EmployeePaymentsPage() {
  const queryClient = useQueryClient();

  // Estado para mês/ano selecionado (padrão: mês atual)
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // Data mínima permitida (julho/2026 - quando a funcionalidade foi criada)
  const minDate = new Date(2026, 6, 1); // Julho = mês 6 (0-indexed)

  // Handler para mudar mês com validação
  const handleMonthChange = (month: number) => {
    const selectedDate = new Date(selectedYear, month - 1, 1);

    // Se tentar selecionar data anterior a julho/2026
    if (selectedDate < minDate) {
      setSelectedMonth(7); // Julho
      setSelectedYear(2026);
      toast.error('Não é possível selecionar meses anteriores a julho/2026');
      return;
    }

    setSelectedMonth(month);
  };

  // Handler para mudar ano com validação
  const handleYearChange = (year: number) => {
    const selectedDate = new Date(year, selectedMonth - 1, 1);

    // Se tentar selecionar data anterior a julho/2026
    if (selectedDate < minDate) {
      setSelectedMonth(7); // Julho
      setSelectedYear(2026);
      toast.error('Não é possível selecionar meses anteriores a julho/2026');
      return;
    }

    setSelectedYear(year);
  };

  // Estado do modal de QR Code
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeePaymentSummary | null>(null);
  const [pixQRCode, setPixQRCode] = useState<PixQRCodeResult | null>(null);
  const [copiedPayload, setCopiedPayload] = useState(false);
  const [copiedPixKey, setCopiedPixKey] = useState(false);

  // Diálogo de adicionais (valores lançados à mão para o colaborador no mês)
  const [additionalsEmployee, setAdditionalsEmployee] = useState<{ id: string; name: string; vtPixAmount: number } | null>(null);

  // Estado de ordenação
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Query para buscar pagamentos
  const { data: paymentsResponse, isLoading } = useQuery({
    queryKey: ['employee-payments', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await employeePaymentsAPI.list(selectedMonth, selectedYear);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // Cache por 2 minutos
  });

  const payments = (paymentsResponse?.data || []) as EmployeePaymentSummary[];
  const metadata = paymentsResponse?.metadata;
  const isMonthClosed = metadata?.isClosed ?? false;

  // Query para buscar benefícios
  const { data: benefitsResponse } = useQuery({
    queryKey: ['employee-benefits', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await employeePaymentsAPI.listBenefits(selectedMonth, selectedYear);
      return response.data;
    },
    // Sem cache: mudanças no cadastro (ex.: VT no Pix) precisam aparecer ao voltar para a página
    staleTime: 0,
  });

  const benefits = (benefitsResponse?.data || []) as EmployeeBenefitSummary[];

  // Ordenar pagamentos
  const sortedPayments = useMemo(() => {
    if (!sortDirection) return payments;

    return [...payments].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.employeeName.localeCompare(b.employeeName);
          break;
        case 'salary':
          comparison = (a.details.salary || 0) - (b.details.salary || 0);
          break;
        case 'commissions':
          comparison = (a.details.commissionsAmount || 0) - (b.details.commissionsAmount || 0);
          break;
        case 'additionals':
          comparison = additionalsTotal(a) - additionalsTotal(b);
          break;
        case 'profitAdvance':
          comparison = (a.details.profitAdvanceAmount || 0) - (b.details.profitAdvanceAmount || 0);
          break;
        case 'total':
          comparison = a.totalAmount - b.totalAmount;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [payments, sortField, sortDirection]);

  // Handler para mudar ordenação
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Ciclar: asc -> desc -> asc
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Mutation para gerar QR Code
  const generateQRCodeMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      const employee = payments.find((p) => p.employeeId === employeeId);
      if (!employee) throw new Error('Colaborador não encontrado');

      const response = await employeePaymentsAPI.generatePixQRCode(employeeId, employee.totalAmount);
      return response.data.data;
    },
    onSuccess: (data) => {
      setPixQRCode(data);
      toast.success('QR Code gerado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao gerar QR Code');
    },
  });

  // Mutation para marcar como pago
  const markAsPaidMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      await employeePaymentsAPI.markAsPaid(employeeId, selectedMonth, selectedYear);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-payments', selectedMonth, selectedYear] });
      toast.success('Pagamento marcado como pago!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao marcar pagamento como pago');
    },
  });

  // Mutation para desmarcar como pago
  const markAsUnpaidMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      await employeePaymentsAPI.markAsUnpaid(employeeId, selectedMonth, selectedYear);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-payments', selectedMonth, selectedYear] });
      toast.success('Pagamento desmarcado como pago!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao desmarcar pagamento');
    },
  });

  // Calcular totais
  const totalAmount = payments.reduce((sum, p) => sum + p.totalAmount, 0);
  const totalEmployees = payments.length;

  // Handler para abrir modal de QR Code
  const handleOpenQRCode = (employee: EmployeePaymentSummary) => {
    if (!employee.pixKey) {
      toast.error('Colaborador não possui chave PIX cadastrada');
      return;
    }

    setSelectedEmployee(employee);
    setPixQRCode(null);
    setCopiedPayload(false);
    setCopiedPixKey(false);
    setQrCodeDialogOpen(true);

    // Gerar QR Code
    generateQRCodeMutation.mutate(employee.employeeId);
  };

  // Handler para copiar payload PIX
  const handleCopyPayload = async () => {
    if (!pixQRCode?.payload) return;

    try {
      await navigator.clipboard.writeText(pixQRCode.payload);
      setCopiedPayload(true);
      toast.success('Código PIX copiado!');

      setTimeout(() => setCopiedPayload(false), 3000);
    } catch (error) {
      toast.error('Erro ao copiar código PIX');
    }
  };

  // Handler para copiar chave PIX
  const handleCopyPixKey = async () => {
    if (!selectedEmployee?.pixKey) return;

    try {
      await navigator.clipboard.writeText(selectedEmployee.pixKey);
      setCopiedPixKey(true);
      toast.success('Chave PIX copiada!');

      setTimeout(() => setCopiedPixKey(false), 3000);
    } catch (error) {
      toast.error('Erro ao copiar chave PIX');
    }
  };

  // Componente para renderizar ícone de ordenação
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4 opacity-40" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Pagamentos</h1>
          <p className="text-muted-foreground">
            Valores a pagar para cada colaborador
          </p>
        </div>

        <MonthYearSelector
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={handleMonthChange}
          onYearChange={handleYearChange}
          disableFutureMonths={true}
        />
      </div>

      {/* Banner de Mês Fechado */}
      {isMonthClosed && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <Lock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                  Mês Fechado - Valores Históricos Protegidos
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Este mês foi fechado no financeiro em{' '}
                  {metadata?.closedAt
                    ? new Date(metadata.closedAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'data desconhecida'}
                  . Os valores de salário, comissão e antecipação estão congelados e não serão
                  alterados automaticamente, garantindo o histórico exato do que foi pago neste
                  período.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs: Salários e Benefícios */}
      <Tabs defaultValue="salaries" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="salaries" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Salários
          </TabsTrigger>
          <TabsTrigger value="benefits" className="flex items-center gap-2">
            <Gift className="h-4 w-4" />
            Benefícios
          </TabsTrigger>
        </TabsList>

        {/* Aba de Salários */}
        <TabsContent value="salaries" className="mt-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total de Colaboradores</p>
                <p className="text-2xl font-bold">{totalEmployees}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total a Pagar</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {formatCurrency(totalAmount)}
                </p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>Nenhum pagamento para o período selecionado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center hover:text-foreground transition-colors"
                      >
                        Colaborador/Sócio
                        <SortIcon field="name" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button
                        onClick={() => handleSort('salary')}
                        className="flex items-center ml-auto hover:text-foreground transition-colors"
                      >
                        Salário
                        <SortIcon field="salary" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button
                        onClick={() => handleSort('commissions')}
                        className="flex items-center ml-auto hover:text-foreground transition-colors"
                      >
                        Comissões
                        <SortIcon field="commissions" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right" title="Valores lançados à mão (ex.: comissão das torres), que somam como comissão inclusive no INSS e FGTS, e o VT pago no Pix (automático, sem INSS/FGTS).">
                      <button
                        onClick={() => handleSort('additionals')}
                        className="flex items-center ml-auto hover:text-foreground transition-colors"
                      >
                        Adicionais
                        <SortIcon field="additionals" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button
                        onClick={() => handleSort('profitAdvance')}
                        className="flex items-center ml-auto hover:text-foreground transition-colors"
                      >
                        Antecipação
                        <SortIcon field="profitAdvance" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right text-red-600 dark:text-red-400">
                      Desconto VT
                    </TableHead>
                    <TableHead className="text-right text-orange-600 dark:text-orange-400">
                      Desconto INSS
                    </TableHead>
                    <TableHead className="text-right text-orange-600 dark:text-orange-400" title="IRRF retido do CLT: tabela progressiva sobre (rendimento − INSS ou desconto simplificado), com o redutor da Lei 15.270 (isento até R$ 5.000).">
                      IRRF
                    </TableHead>
                    <TableHead className="text-right text-purple-600 dark:text-purple-400">
                      Descontos Acordos
                    </TableHead>
                    <TableHead className="text-right">
                      <button
                        onClick={() => handleSort('total')}
                        className="flex items-center ml-auto hover:text-foreground transition-colors"
                      >
                        Total Líquido
                        <SortIcon field="total" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right text-muted-foreground" title="FGTS depositado pela empresa (8% sobre salário + comissões + adicionais − acordos). Não é descontado do colaborador.">
                      FGTS <span className="text-xs font-normal">(empresa)</span>
                    </TableHead>
                    <TableHead>Chave PIX</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedPayments.map((payment) => (
                    <TableRow key={payment.employeeId}>
                      <TableCell className="font-medium">{payment.employeeName}</TableCell>
                      <TableCell className="text-right text-sm">
                        {payment.details.salary
                          ? formatCurrency(payment.details.salary)
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {payment.details.commissionsAmount ? (
                          <div className="flex flex-col items-end">
                            <span className="font-medium">
                              {formatCurrency(payment.details.commissionsAmount)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({payment.details.commissionsCount} {payment.details.commissionsCount === 1 ? 'item' : 'itens'})
                            </span>
                          </div>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {additionalsTotal(payment) ? (
                          <button
                            type="button"
                            onClick={() =>
                              setAdditionalsEmployee({
                                id: payment.employeeId,
                                name: payment.employeeName,
                                vtPixAmount: payment.details.vtPixAmount || 0,
                              })
                            }
                            className="inline-flex flex-col items-end hover:underline"
                            title={additionalsItems(payment)
                              .map((a) => `${a.description}: ${formatCurrency(a.amount)}`)
                              .join('\n')}
                          >
                            <span className="font-medium">{formatCurrency(additionalsTotal(payment))}</span>
                            <span className="text-xs text-muted-foreground">
                              ({additionalsItems(payment).length}{' '}
                              {additionalsItems(payment).length === 1 ? 'item' : 'itens'})
                            </span>
                          </button>
                        ) : isMonthClosed ? (
                          '-'
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-muted-foreground"
                            onClick={() => setAdditionalsEmployee({ id: payment.employeeId, name: payment.employeeName, vtPixAmount: 0 })}
                            aria-label={`Lançar adicional para ${payment.employeeName}`}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {payment.details.profitAdvanceAmount
                          ? formatCurrency(payment.details.profitAdvanceAmount)
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right text-sm text-red-600 dark:text-red-400">
                        {payment.details.vtDiscount
                          ? `- ${formatCurrency(payment.details.vtDiscount)}`
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right text-sm text-orange-600 dark:text-orange-400">
                        {payment.details.inssDiscount
                          ? `- ${formatCurrency(payment.details.inssDiscount)}`
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right text-sm text-orange-600 dark:text-orange-400">
                        {payment.details.irrfDiscount
                          ? `- ${formatCurrency(payment.details.irrfDiscount)}`
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right text-sm text-purple-600 dark:text-purple-400">
                        {payment.details.agreementDiscounts
                          ? `- ${formatCurrency(payment.details.agreementDiscounts)}`
                          : '-'}
                      </TableCell>
                      <TableCell
                        className={`text-right font-semibold ${
                          payment.totalAmount === 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-green-600 dark:text-green-400'
                        }`}
                      >
                        {formatCurrency(payment.totalAmount)}
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {payment.details.fgts ? formatCurrency(payment.details.fgts) : '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {payment.pixKey ? (
                          <span className="text-green-600 dark:text-green-400">
                            {payment.pixKey.length > 20
                              ? `${payment.pixKey.substring(0, 20)}...`
                              : payment.pixKey}
                          </span>
                        ) : (
                          <span className="text-orange-600 dark:text-orange-400">
                            Não cadastrado
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {payment.isPaid ? (
                          <Badge variant="success" className="gap-1">
                            <Check className="h-3 w-3" />
                            Pago
                          </Badge>
                        ) : (
                          <Badge variant="warning" className="gap-1">
                            <X className="h-3 w-3" />
                            Pendente
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant={payment.pixKey && payment.totalAmount > 0 ? 'default' : 'outline'}
                            disabled={!payment.pixKey || payment.totalAmount === 0 || payment.isPaid}
                            onClick={() => handleOpenQRCode(payment)}
                          >
                            <QrCode className="h-4 w-4 mr-2" />
                            QR Code
                          </Button>
                          {payment.isPaid ? (
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 w-[136px]"
                              onClick={() => markAsUnpaidMutation.mutate(payment.employeeId)}
                              disabled={markAsUnpaidMutation.isPending}
                            >
                              <X className="h-4 w-4 mr-2" />
                              Desmarcar
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 w-[136px]"
                              onClick={() => markAsPaidMutation.mutate(payment.employeeId)}
                              disabled={markAsPaidMutation.isPending}
                            >
                              <Check className="h-4 w-4 mr-2" />
                              Marcar Pago
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Aba de Benefícios */}
        <TabsContent value="benefits" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                    <Gift className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total de Colaboradores com Benefícios</p>
                    <p className="text-2xl font-bold">{benefits.length}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total em Benefícios</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(benefits.reduce((sum, b) => sum + b.totalBenefits, 0))}
                    </p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {benefits.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Gift className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>Nenhum benefício cadastrado para o período selecionado</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[30%]">Colaborador</TableHead>
                        <TableHead className="text-right w-[17.5%]">VA/VR (Mensal)</TableHead>
                        <TableHead className="text-right w-[17.5%]">Home Office (Mensal)</TableHead>
                        <TableHead className="text-right w-[17.5%]">VT (Mensal)</TableHead>
                        <TableHead className="text-right w-[17.5%]">Total Benefícios</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {benefits.map((benefit) => (
                        <TableRow key={benefit.employeeId}>
                          <TableCell className="font-medium w-[30%]">{benefit.employeeName}</TableCell>
                          <TableCell className="text-right w-[17.5%]">
                            <span className="inline-block min-w-[100px]">
                              {benefit.benefitVAVR ? formatCurrency(benefit.benefitVAVR) : '-'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right w-[17.5%]">
                            <span className="inline-block min-w-[100px]">
                              {benefit.benefitHomeOffice ? formatCurrency(benefit.benefitHomeOffice) : '-'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right w-[17.5%]">
                            <span className="inline-block min-w-[100px]">
                              {benefit.benefitVT ? formatCurrency(benefit.benefitVT) : '-'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right w-[17.5%]">
                            <span className="inline-block min-w-[100px] font-semibold text-green-600 dark:text-green-400">
                              {formatCurrency(benefit.totalBenefits)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog de Adicionais */}
      <EmployeeAdditionalsDialog
        open={!!additionalsEmployee}
        onOpenChange={(open) => !open && setAdditionalsEmployee(null)}
        employee={additionalsEmployee}
        month={selectedMonth}
        year={selectedYear}
        isMonthClosed={isMonthClosed}
        vtPixAmount={additionalsEmployee?.vtPixAmount ?? 0}
      />

      {/* Dialog de QR Code */}
      <Dialog open={qrCodeDialogOpen} onOpenChange={setQrCodeDialogOpen}>
        <DialogContent className="sm:max-w-md px-6 pt-6 pb-6">
          <DialogHeader>
            <DialogTitle>Pagamento via PIX</DialogTitle>
            <DialogDescription>
              {selectedEmployee && (
                <span>
                  {selectedEmployee.employeeName} - {formatCurrency(selectedEmployee.totalAmount)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Loading */}
            {generateQRCodeMutation.isPending && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}

            {/* Error */}
            {generateQRCodeMutation.isError && (
              <div className="text-center py-8 text-red-600">
                <p>Erro ao gerar QR Code</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => selectedEmployee && generateQRCodeMutation.mutate(selectedEmployee.employeeId)}
                >
                  Tentar novamente
                </Button>
              </div>
            )}

            {/* QR Code */}
            {pixQRCode && (
              <>
                {/* Imagem do QR Code */}
                <div className="flex justify-center py-6 px-6 bg-white dark:bg-gray-800 rounded-lg">
                  <img
                    src={pixQRCode.qrCode}
                    alt="QR Code PIX"
                    className="w-64 h-64 object-contain"
                  />
                </div>

                {/* Informações */}
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between items-start gap-6">
                    <span className="text-muted-foreground">Chave PIX:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-right break-all">{selectedEmployee?.pixKey}</span>
                      <button
                        onClick={handleCopyPixKey}
                        className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                        title="Copiar chave PIX"
                      >
                        {copiedPixKey ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between gap-6">
                    <span className="text-muted-foreground">Valor:</span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(pixQRCode.amount)}
                    </span>
                  </div>
                </div>

                {/* Botão Copiar Código PIX */}
                <div className="pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleCopyPayload}
                    disabled={copiedPayload}
                  >
                    {copiedPayload ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                        Código Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" />
                        Copiar Código PIX (Pix Copia e Cola)
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
